import { Component, HostListener, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { CommonConstants } from 'src/app/constants/commonConstants';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { LoginService } from 'src/app/services/login/login.service';
import { ProfessionalService } from 'src/app/services/professional/professional.service';
import { ConsumersRegModel } from 'src/app/models/consumersModel';
import { ConsumerService } from 'src/app/services/consumer.service';

@Component({
  selector: 'app-consumer-signup',
  templateUrl: './consumer-signup.component.html',
  styleUrls: ['./consumer-signup.component.scss']
})
export class ConsumerSignupComponent implements OnInit {

  consumerSignUpForm: FormGroup;
  showBenefits: boolean = true;
  isMobile: boolean = false;
  submitted = false;
  xClientId: string;
  mlsAffiliates: any[];
  termsAndConditons: any;
  tcURL: SafeResourceUrl;
  tcHeader: string = '';
  androidStoreUrl: string;
  iOSStoreUrl: string;

  // Drives which download link the success modal offers. Set once from the user agent.
  isMobileDevice = false;
  isIOSDevice = false;
  // AC-4594: "Prefer the app?" badges above the form. The Play link carries the invite code
  // as the install referrer, so the Android app opens signup with the invite filled in.
  showPlayStoreBadge = false;
  showAppStoreBadge = false;
  inviteAndroidStoreUrl: string;
  displayEmailError: boolean = false;
  errorMessage: string = '';
  uniqueId: string;
  registrationCode: string;
  // The invite is past the inviter's free passes: the form says a membership payment follows.
  isPaidInvite = false;

  /**
   * Contacts 3-5 are optional, so they sit behind a toggle rather than making every
   * consumer scroll past five pairs of fields. Same treatment as the agent and
   * realtor signup pages, which hide their own contacts 3-5 the same way.
   *
   * Starts collapsed: with two required contacts above it, the optional three are
   * the part most people will skip.
   */
  showOptionalContacts: boolean = false;

  /**
   * AC-4441 - five emergency-contact slots, the first two required.
   *
   * Two is not a UI choice: EnrollProfessional already enforces it in T-SQL
   * ("two complete emergency contacts are required"), and SavePersonEmergencyContacts
   * enforces it for consumers. Changing REQUIRED_EMERGENCY_CONTACTS here alone would
   * only move where the user is told no.
   *
   * Slot 1 carries no numeric suffix, matching the API's EmergencyContactName /
   * EmergencyContactName2 property names.
   */
  readonly REQUIRED_EMERGENCY_CONTACTS = 2;
  readonly TOTAL_EMERGENCY_CONTACTS = 5;

  /**
   * Validates on the digits, not the raw text. A pattern validator on the raw value
   * rejected "3102106967 " - mobile keyboards append a space after a suggestion or
   * autofill - and "(310) 210-6967", both perfectly good numbers. Formatting
   * characters are tolerated; anything else (letters etc.) is still an error.
   */
  readonly phoneValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value || '').toString().trim();
    if (!value) {
      return null; // Validators.required owns the empty case
    }
    const digits = value.replace(/\D/g, '');
    const valid = /^[\d\s+\-().]+$/.test(value) && digits.length >= 10 && digits.length <= 12;
    return valid ? null : { pattern: true };
  };

  readonly emergencyContactSlots = Array.from(
    { length: this.TOTAL_EMERGENCY_CONTACTS },
    (_, i) => {
      const index = i + 1;
      const suffix = index === 1 ? '' : `${index}`;
      return {
        index,
        suffix,
        required: index <= this.REQUIRED_EMERGENCY_CONTACTS,
        // AC-4540: entered as first + last name, sent as the one EmergencyContactName
        // the API and database store - see contactName().
        firstNameControl: `emergencyContactFirstName${suffix}`,
        lastNameControl: `emergencyContactLastName${suffix}`,
        phoneControl: `emergencyContactNumber${suffix}`
      };
    });

  /**
   * Split once, not in a template getter. A getter would hand *ngFor a brand-new
   * array on every change-detection pass and re-render the rows each time.
   */
  readonly requiredContactSlots = this.emergencyContactSlots.filter(slot => slot.required);
  readonly optionalContactSlots = this.emergencyContactSlots.filter(slot => !slot.required);

  constructor(
    private formBuilder: FormBuilder,
    private consumerService: ConsumerService,
    private professionalService: ProfessionalService,
    private environmentService: EnvironmentService,
    private loginService: LoginService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private spinner: NgxSpinnerService) { }

  ngOnInit(): void {
    const routeParams = this.activatedRoute.snapshot.paramMap;
    this.uniqueId = sessionStorage.getItem('UNIQUE_ID');
    this.registrationCode = routeParams.get('regCode');

    this.xClientId = this.environmentService.getXClientId();
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + 1);
    this.androidStoreUrl = this.environmentService.getRescueAndroidStoreUrl();
    this.iOSStoreUrl = this.environmentService.getRescueiOSStoreUrl();
    this.showPlayStoreBadge = this.environmentService.hasRescuePlayStoreLink();
    this.showAppStoreBadge = this.environmentService.hasRescueAppStoreLink();
    this.inviteAndroidStoreUrl = this.environmentService.getRescueAndroidStoreUrl(this.registrationCode);
    this.detectDevicePlatform();
    this.getMLSAffilates();
    this.buildSignUpForm();
    this.showHideBenefits(window.innerWidth);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.showHideBenefits(event.target.innerWidth);
  }

  showHideBenefits(width) {
    if (width <= 1023) {
      if (this.isMobile == false) {
        this.isMobile = true;
        this.showBenefits = false;
      }
    }
    else {
      this.isMobile = false;
      this.showBenefits = true;
    }
  }

  getMLSAffilates() {
    const url = this.environmentService.getIA9V2Url() + "MlsAffiliates/?code=" + this.environmentService.getIA9V2Key();
    this.spinner.show();
    this.professionalService.getMLSAffiliates(url).subscribe(response => {
      this.spinner.hide();
      this.mlsAffiliates = response;
    });
  }

  buildSignUpForm() {
    const controls = {
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      phone: new FormControl(null, [Validators.required, this.phoneValidator]),
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required, Validators.minLength(8)]),
      confirmPassword: new FormControl('', [Validators.required]),
      registrationCode: new FormControl(''),
      agreeTandC: new FormControl(false, [Validators.requiredTrue])
    };

    // AC-4441: a consumer has no agent record, so these are the only emergency
    // contacts domiiRescue will ever have for them. Driven off the slot metadata so
    // the count and the required-count live in one place.
    this.emergencyContactSlots.forEach(slot => {
      const validators = slot.required ? [Validators.required] : [];
      controls[slot.firstNameControl] = new FormControl('', validators);
      controls[slot.lastNameControl] = new FormControl('', validators);
      controls[slot.phoneControl] = new FormControl('',
        slot.required
          ? [Validators.required, this.phoneValidator]
          : [this.phoneValidator]);
    });

    this.consumerSignUpForm = this.formBuilder.group(controls, {
      validators: [this.mustMatch('password', 'confirmPassword'), this.validateEmergencyContacts()]
    });

    // The invite code can arrive two ways, and the route param has to win. A rescue
    // link lands on /consumersignup/:regCode, so that is where the code is for
    // everyone who follows an invite. Previously only sessionStorage was applied and
    // the route param was read into a field and never used, which is why the field
    // came up empty for anyone arriving from a shared link.
    const invitedCode = this.registrationCode || this.uniqueId;
    if (invitedCode) {
      this.consumerSignUpForm.patchValue({ registrationCode: invitedCode });
      this.lookUpInvite(invitedCode);
    }
  }

  /**
   * Finds out up front whether the invite is past the inviter's free passes, so the form can say a
   * membership payment follows account creation. Signup itself never depends on it: on any error
   * the notice just stays hidden, and buyer/register's upgradeCode still routes to payment.
   */
  private lookUpInvite(code: string) {
    const url = this.environmentService.getIA9LightningUrl() + 'registration/validate/rescueinvite/'
      + encodeURIComponent(code) + '?code=' + this.environmentService.getIA9LightningKey();
    this.consumerService.getRescueInvite(url, this.xClientId).subscribe(
      response => this.isPaidInvite = !!response?.data?.isPaidInvite,
      () => this.isPaidInvite = false);
  }

  /**
   * True when the code was supplied for us. The field is then shown read-only:
   * the value still has to reach the API, and it is what ties this signup back to
   * the invite that was sent, so it must not be editable or clearable.
   */
  get isInviteCodeReadOnly(): boolean {
    return !!(this.registrationCode || this.uniqueId);
  }


  /**
   * Opens the optional panel when something inside it is wrong, so a validation
   * message can never end up hidden behind a collapsed section. Only the
   * cross-field rules can fail down there - slots 3-5 carry no required validator -
   * so this checks a duplicate or self-number against those slots specifically.
   */
  revealOptionalContactsOnError(): void {
    if (this.showOptionalContacts) {
      return;
    }

    const mobile = this.normalizePhone(this.consumerSignUpForm.get('phone')?.value);
    const allNumbers = this.emergencyContactSlots
      .map(slot => this.normalizePhone(this.consumerSignUpForm.get(slot.phoneControl)?.value));

    const hasProblem = this.optionalContactSlots.some(slot => {
      const name = this.contactName(this.consumerSignUpForm, slot);
      const phone = this.normalizePhone(this.consumerSignUpForm.get(slot.phoneControl)?.value);

      if (!name && !phone) {
        return false;
      }
      // Half-filled row, own mobile, or a number that also appears in another slot.
      return (!name || !phone)
        || (!!mobile && phone === mobile)
        || allNumbers.filter(number => number && number === phone).length > 1;
    });

    if (hasProblem) {
      this.showOptionalContacts = true;
    }
  }

  /**
   * Compares two numbers the way the API and the database do - on the last ten
   * digits. Comparing full strings, as an earlier revision did, let
   * "+15551234567" and "5551234567" through the browser only to be rejected by
   * SQL, because EnrollProfessional and dbo.fn_PhoneLast10 both compare
   * RIGHT(stripped, 10).
   */
  normalizePhone(phone: string): string {
    const digits = (phone || '').replace(/\D/g, '');
    return digits.length <= 10 ? digits : digits.slice(-10);
  }

  /**
   * A contact's name as the API stores it: first and last joined. The form asks for
   * them separately (AC-4540 layout) but the API and database keep one name per contact.
   */
  contactName(group: FormGroup, slot: { firstNameControl: string; lastNameControl: string }): string {
    const first = (group.get(slot.firstNameControl)?.value || '').trim();
    const last = (group.get(slot.lastNameControl)?.value || '').trim();
    return `${first} ${last}`.trim();
  }

  /** Emergency contacts must be distinct from each other and from your own mobile. */
  validateEmergencyContacts() {
    return (group: FormGroup) => {
      const errors: any = {};
      const mobile = this.normalizePhone(group.get('phone')?.value);

      const entered = this.emergencyContactSlots
        .map(slot => this.normalizePhone(group.get(slot.phoneControl)?.value))
        .filter(number => !!number);

      if (entered.some((number, index) => entered.indexOf(number) !== index)) {
        errors.duplicateEmergencyNumbers = true;
      }

      if (mobile && entered.includes(mobile)) {
        errors.emergencyIsOwnNumber = true;
      }

      // A name without a number, or the reverse, is not a usable contact - an alert
      // has to say who is calling.
      const incomplete = this.emergencyContactSlots.some(slot => {
        const name = this.contactName(group, slot);
        const phone = this.normalizePhone(group.get(slot.phoneControl)?.value);
        return (!!name && !phone) || (!name && !!phone);
      });
      if (incomplete) {
        errors.incompleteEmergencyContact = true;
      }

      return Object.keys(errors).length ? errors : null;
    };
  }

  mustMatch(controlName: string, matchingControlName: string) {
    return (group: FormGroup) => {
      const control = group.controls[controlName];
      const matchingControl = group.controls[matchingControlName];
      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        return;
      }

      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      }
      else {
        matchingControl.setErrors(null);
      }
    }
  }

  get form() { return this.consumerSignUpForm.controls; }

  onSubmit() {
    this.submitted = true;
    if (this.consumerSignUpForm.invalid) {
      // Expand the optional contacts if that is where the problem is, otherwise the
      // form just refuses to submit with nothing on screen explaining why.
      this.revealOptionalContactsOnError();
      return;
    }
    this.registerUser();
  }

  registerUser() {
    const consumerPayload = new ConsumersRegModel();
    consumerPayload.firstName = this.consumerSignUpForm.get('firstName').value;
    consumerPayload.lastName = this.consumerSignUpForm.get('lastName').value;
    // Strip spaces/brackets/dashes the validator tolerates; keep a leading "+".
    consumerPayload.phone = (this.consumerSignUpForm.get('phone').value || '').replace(/[^\d+]/g, '');
    consumerPayload.email = this.consumerSignUpForm.get('email').value;
    consumerPayload.password = this.consumerSignUpForm.get('password').value;
    consumerPayload.registrationCode = this.consumerSignUpForm.get('registrationCode').value;

    // Send digits only. The API compares on the last ten and the sproc stores what it
    // is given, so normalising here keeps one representation in the database.
    this.emergencyContactSlots.forEach(slot => {
      consumerPayload[`emergencyContactName${slot.suffix}`] =
        this.contactName(this.consumerSignUpForm, slot);
      consumerPayload[`emergencyContactNumber${slot.suffix}`] =
        this.normalizePhone(this.consumerSignUpForm.get(slot.phoneControl).value);
    });

    this.spinner.show();
    const url = this.environmentService.getIA9LightningUrl() + 'buyer/register?code=' + this.environmentService.getIA9LightningKey();
    this.consumerService.registerConsumer(url, consumerPayload, this.xClientId).subscribe(response => {
      this.spinner.hide();
      // Paid invite: the account exists on the free plan; the payment step comes next. The
      // payment page offers "continue with the free version", so this never blocks signup.
      if (response?.upgradeCode) {
        this.router.navigate(['consumer/payment', response.upgradeCode], { queryParams: { signup: 1 } });
        return;
      }
      // Stay on the confirmation: it carries the App Store and Play Store links, which
      // are the next step for someone who just signed up from a text message. The old
      // redirect to the marketing site bounced them away from it after two seconds.
      document.getElementById('openRegSuccessBtn').click();
    }, error => {
      console.log(error);
      this.spinner.hide();
      // Surface what the API said - it distinguishes an invalid invite code from an
      // unusable set of emergency contacts, and "something went wrong" does not.
      this.errorMessage = error?.error?.description || '';
      document.getElementById('openRegFailedBtn').click();
    });
  }

  openTerms(event, type) {
    event.preventDefault();
    document.getElementById('openTermsConditionsBtn').click();
    (document.getElementById('t&CLogo') as HTMLImageElement).src = CommonConstants.imageBasePath + this.environmentService.getCompanyLogo();
    let url = '';
    if (type == 'TC') {
      this.tcHeader = 'Terms and Conditions';
      url = this.environmentService.getEULAUrl();
    }
    else if (type == 'PP') {
      this.tcHeader = 'Privacy Policy';
      url = this.environmentService.getPrivacyUrl();
    }
    this.tcURL = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  login() {
    this.loginService.login();
  }

  openAppStore() {
    window.open(this.iOSStoreUrl);
  }

  openPlayStore() {
    window.open(this.androidStoreUrl);
  }

  /**
   * Reads the platform off the user agent so the success modal can send a phone straight to
   * its own store instead of making someone pick.
   *
   * iPadOS 13 and later report themselves as "Macintosh", so the Mac branch only counts as
   * iOS when the device also reports touch points - without that, every iPad is offered the
   * desktop choice.
   */
  private detectDevicePlatform() {
    const userAgent = navigator.userAgent || '';
    const isIPadOS = /Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;

    this.isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) || isIPadOS;
    this.isMobileDevice = this.isIOSDevice || /Android/i.test(userAgent);
  }

  /**
   * The single "tap this link" target. On a desktop the user agent names no store, so this
   * falls back to the Apple listing - the storeChoice row is shown there too, so the other
   * store is never more than one click away.
   */
  openStoreForDevice() {
    if (this.isIOSDevice) {
      this.openAppStore();
      return;
    }

    if (this.isMobileDevice) {
      this.openPlayStore();
      return;
    }

    this.openAppStore();
  }

  validateEmail(event: any) {
    const url = this.environmentService.getIA9LightningUrl() + 'registration/validate/email/' + event.target.value + '?code=' + this.environmentService.getIA9LightningKey();
    this.spinner.show();
    this.professionalService.getValidEmail(url, this.xClientId).subscribe((response) => {
      this.displayEmailError = false;
      this.spinner.hide();
    }, (error) => {
      this.spinner.hide();
      if (error.error.statusCode === 8002) {
        this.displayEmailError = true;
        this.errorMessage = 'An account with email address already exists.';
      }
    })
  }

  redirectToLogin() {
    this.loginService.login();
    this.displayEmailError = false;
  }

}
